import { z } from 'zod';
import { combinedDateTime } from '@/lib/utils';

export const MAX_DELIVERY_UPDATE_IMAGES = 50;
export const MAX_DELIVERY_UPDATE_IMAGE_SIZE_BYTES = 20 * 1024 * 1024;

export const ALLOWED_DELIVERY_UPDATE_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heif',
  'image/heic',
  'image/raw',
] as const;

export const ALLOWED_DELIVERY_UPDATE_IMAGE_EXTENSIONS = [
  'jpeg',
  'jpg',
  'png',
  'heif',
  'heic',
  'raw',
  'cr2',
  'nef',
  'arw',
  'dng',
] as const;

export const isAllowedDeliveryUpdateImageFile = (file: File): boolean => {
  if (
    file.type &&
    (ALLOWED_DELIVERY_UPDATE_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)
  ) {
    return true;
  }
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return (ALLOWED_DELIVERY_UPDATE_IMAGE_EXTENSIONS as readonly string[]).includes(ext);
};

const toDateOnlyTimestamp = (date: Date): number => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized.getTime();
};

const isBeforeDateOnly = (value: Date, minimum: Date): boolean =>
  toDateOnlyTimestamp(value) < toDateOnlyTimestamp(minimum);

const isAfterDateOnly = (value: Date, maximum: Date): boolean =>
  toDateOnlyTimestamp(value) > toDateOnlyTimestamp(maximum);

// Context type for validation that includes work order dates
export interface DeliveryUpdateValidationContext {
  workOrderCreatedDate?: Date;
  pickupDate?: Date;
  deliveryDate?: Date;
}

export const AdditionalFeeSchema = z.object({
  feeId: z.string().uuid().optional(),
  billingItemId: z.string().uuid().optional().nullable(),
  feeType: z.enum(['Tolls', 'Parking Fee', 'Filing Fee', 'Other']),
  feeAmount: z.number().positive('Fee amount must be greater than 0'),
});

export const DeliveryUpdateSchema = z
  .object({
    completedDate: z.date({ required_error: 'Completed date is required' }),
    completedTime: z.date({ required_error: 'Completed time is required' }),
    receivedBy: z.string().min(1, 'Received by is required'),
    date: z.date({ required_error: 'Date is required' }), // Date when this delivery update is recorded
    waitingTime: z.date().optional().nullish(),
    hasReturn: z.boolean().optional().default(false), // Return checkbox
    returnDate: z.date().optional().nullish(),
    returnName: z.string().optional().nullish(),
    additionalFees: z.array(AdditionalFeeSchema).optional().default([]),
    images: z
      .array(
        z.object({
          id: z.string().optional().nullish(),
          name: z.string().optional().nullish(),
          image: z.any().optional().nullish(),
          mediaId: z.string().optional().nullish(),
          updateMediaId: z.string().uuid().optional().nullish(),
          mediaData: z.any().optional().nullish(),
        })
      )
      .optional()
      .default([]),
    mediaIds: z.array(z.string()).optional().default([]),
  })
  .superRefine((data, ctx) => {
    // Validate completed date is not in the future
    if (data.completedDate && isAfterDateOnly(data.completedDate, new Date())) {
      ctx.addIssue({
        path: ['completedDate'],
        code: 'custom',
        message: 'Completed date cannot be in the future',
      });
    }

    // Validate completed date time is not in the future
    if (
      data.completedDate &&
      data.completedTime &&
      combinedDateTime(data.completedDate, data.completedTime) > new Date()
    ) {
      ctx.addIssue({
        path: ['completedTime'],
        code: 'custom',
        message: 'Completed time cannot be in the future',
      });
    }

    // Validate return date is not in the future
    if (data.returnDate && data.returnDate > new Date()) {
      ctx.addIssue({
        path: ['returnDate'],
        code: 'custom',
        message: 'Return date cannot be in the future',
      });
    }

    // Validate return fields when hasReturn is checked
    if (data.hasReturn) {
      if (!data.returnName || data.returnName.trim() === '') {
        ctx.addIssue({
          path: ['returnName'],
          code: 'custom',
          message: 'Return name is required when Return is checked',
        });
      }
      if (!data.returnDate) {
        ctx.addIssue({
          path: ['returnDate'],
          code: 'custom',
          message: 'Return date is required when Return is checked',
        });
      }
    }

    // Validate max 50 images
    if (data.images && data.images.length > MAX_DELIVERY_UPDATE_IMAGES) {
      ctx.addIssue({
        path: ['images'],
        code: 'custom',
        message: `Maximum ${MAX_DELIVERY_UPDATE_IMAGES} images allowed`,
      });
    }

    // Validate date is not in the future
    if (data.date && isAfterDateOnly(data.date, new Date())) {
      ctx.addIssue({
        path: ['date'],
        code: 'custom',
        message: 'Date cannot be in the future',
      });
    }
  });

// Factory function to create schema with validation context
export const createDeliveryUpdateSchema = (context?: DeliveryUpdateValidationContext) => {
  return DeliveryUpdateSchema.superRefine((data, ctx) => {
    if (!context) return;

    const { workOrderCreatedDate, pickupDate, deliveryDate } = context;

    // Validate completed date against work order dates (date-only; same calendar day is allowed)
    if (data.completedDate) {
      if (workOrderCreatedDate && isBeforeDateOnly(data.completedDate, workOrderCreatedDate)) {
        ctx.addIssue({
          path: ['completedDate'],
          code: 'custom',
          message: 'Completed date cannot be earlier than work order created date',
        });
      }
      if (pickupDate && isBeforeDateOnly(data.completedDate, pickupDate)) {
        ctx.addIssue({
          path: ['completedDate'],
          code: 'custom',
          message: 'Completed date cannot be earlier than pickup date',
        });
      }
      if (deliveryDate && isBeforeDateOnly(data.completedDate, deliveryDate)) {
        ctx.addIssue({
          path: ['completedDate'],
          code: 'custom',
          message: 'Completed date cannot be earlier than delivery date',
        });
      }
    }

    // Validate date field against work order dates (same constraints as completed date)
    if (data.date) {
      if (workOrderCreatedDate && isBeforeDateOnly(data.date, workOrderCreatedDate)) {
        ctx.addIssue({
          path: ['date'],
          code: 'custom',
          message: 'Date cannot be earlier than work order created date',
        });
      }
      if (pickupDate && isBeforeDateOnly(data.date, pickupDate)) {
        ctx.addIssue({
          path: ['date'],
          code: 'custom',
          message: 'Date cannot be earlier than pickup date',
        });
      }
      if (deliveryDate && isBeforeDateOnly(data.date, deliveryDate)) {
        ctx.addIssue({
          path: ['date'],
          code: 'custom',
          message: 'Date cannot be earlier than delivery date',
        });
      }
    }

    // Validate return date against work order dates (date-only; same calendar day is allowed)
    if (data.hasReturn && data.returnDate) {
      if (workOrderCreatedDate && isBeforeDateOnly(data.returnDate, workOrderCreatedDate)) {
        ctx.addIssue({
          path: ['returnDate'],
          code: 'custom',
          message: 'Return date cannot be earlier than work order created date',
        });
      }
      if (pickupDate && isBeforeDateOnly(data.returnDate, pickupDate)) {
        ctx.addIssue({
          path: ['returnDate'],
          code: 'custom',
          message: 'Return date cannot be earlier than pickup date',
        });
      }
    }
  });
};

export type DeliveryUpdateFormData = z.infer<typeof DeliveryUpdateSchema>;
